import { getByTag } from "locale-codes";

import { Statsig } from "@/env/statsig/client";
import dateFormats from "@/utils/dateFormats";
import getDuration from "@/utils/getDuration";
import {
  freeCampaignDatePacificTime,
  freeCampaignDateShortPacificTime,
} from "@/utils/revupTime";

interface BuildIcon {
  type: string;
  text: string;
  style?: string; // Optional style for custom CSS
}

interface BundleTypeMapValue {
  text: string;
  type: string;
  style?: string;
}

function singlePluralSimple(count: number, word: string): string {
  const expression = `${count} ${word}`;
  if (count > 1) {
    return `${expression}s`;
  }
  return `${expression}`;
}

function IconDuration(content: any): BuildIcon | undefined {
  if (getDuration(content) != "") {
    return {
      type: "time",
      text: getDuration(content),
    };
  }
  return undefined;
}

export function IconVideos(content: any): BuildIcon | undefined {
  if (content?.content_count > 0) {
    return {
      type: "video-filled",
      text: singlePluralSimple(content.content_count, "Video"),
    };
  }
  return undefined;
}

function IconDifficultyType(content: any): BuildIcon | undefined {
  if (content?.difficulty_type) {
    let iconType: string = "";
    switch (content.difficulty_type) {
      case "Beginner":
        iconType = "novice";
        break;
      case "Intermediate":
        iconType = "associate";
        break;
      case "Advanced":
        iconType = "specialist";
        break;
      default:
        iconType = "expert";
    }
    return {
      type: iconType,
      text: content.difficulty_type,
    };
  }
  return undefined;
}

function IconCourses(content: any): BuildIcon | undefined {
  if (content?.content_count > 0) {
    return {
      type: "article",
      text: singlePluralSimple(content.content_count, "Course"),
    };
  }
  return undefined;
}

export function IconCreds(content: any): BuildIcon | undefined {
  if (
    content?.number_of_credits > 0 &&
    (!content?.is_partial ||
      content?.guid === globalThis.env.REV_UP_CAMPAIGN_GUID_EXCEPTION_CE_CREDIT)
  ) {
    const nb_of_credits =
      content?.guid === globalThis.env.REV_UP_CAMPAIGN_GUID_EXCEPTION_CE_CREDIT
        ? 8
        : content?.number_of_credits;
    return {
      type: "ce-credits",
      text: singlePluralSimple(nb_of_credits, "CE credit"),
    };
  }
  return undefined;
}
function IconAssessments(content: any): BuildIcon | undefined {
  if (content?.number_of_assessments > 0) {
    return {
      type: "assessment",
      text: singlePluralSimple(content.number_of_assessments, "assessment"),
    };
  }
  return undefined;
}

export function IconVersion(content: any): BuildIcon | undefined {
  let version: string | undefined;

  // Determine which version field to use based on lab_type
  if (
    content?.lab_type === "cisco-learning-labs" ||
    content?.lab_type === "standalone-labs" ||
    content?.type != "lab"
  ) {
    version = content?.display_version || content?.version;
  }

  // Return icon if version is valid
  if (version && version != "0.0") {
    return {
      type: "all",
      text: "v" + version,
    };
  }
  return undefined;
}
function IconCertification(content: any): BuildIcon | undefined {
  // Only for graded-labs - standalone-labs don't show certification icon
  if (
    content?.lab_type === "graded-labs" ||
    content?.lab_type === "ccie-practice-labs"
  ) {
    let certifications;
    if (content?.certifications_data?.length > 0) {
      // Get unique certification names from certifications_data
      const uniqueCerts = [
        ...new Set(
          content.certifications_data.map(
            (cert: any) => cert.certification_name
          )
        ),
      ];
      certifications = uniqueCerts;
    } else {
      certifications = content.certifications;
    }

    if (certifications?.length > 0) {
      return {
        type: "certification",
        text: certifications.join(", "),
      };
    }
  }
  return undefined;
}

function IconLabs(content: any): BuildIcon | undefined {
  if (content?.number_of_labs > 0) {
    return {
      type: "lab",
      text: singlePluralSimple(content.number_of_labs, "Lab"),
    };
  }
  return undefined;
}

function IconNodes(content: any): BuildIcon | undefined {
  if (content?.nodes > 0) {
    return {
      type: "node-filled",
      text: singlePluralSimple(content.nodes, "node"),
    };
  }
  return undefined;
}

function IconCalendar(content: any): BuildIcon | undefined {
  if (content?.event_date) {
    return {
      type: "enter-globe",
      text: dateFormats.monthAndYear(content?.event_date),
    };
  }
  return undefined;
}

function IconCreate(content: any): BuildIcon | undefined {
  if (content?.created_at) {
    return {
      type: "event",
      text: "Created " + dateFormats.monthYearComma(content.created_at),
    };
  }
  return undefined;
}
function IconUpdate(content: any): BuildIcon | undefined {
  if (content?.updated_at) {
    return {
      type: "event",
      text: "Updated " + dateFormats.monthYearComma(content.updated_at),
    };
  }
  return undefined;
}

function IconExamFormat(content: any): BuildIcon | undefined {
  if (content?.exam_format_id) {
    switch (content.exam_format_id) {
      case 1:
        return {
          type: "assessment",
          text: "Written exam",
        };
      case 2:
        return {
          type: "lab",
          text: "Lab exam",
        };
      default:
        return undefined;
    }
  }
  return undefined;
}

export function IconBundleContents(content: any): Array<BuildIcon> | undefined {
  const typesMap: Record<string, BundleTypeMapValue> = {
    path: {
      text: "Learning Path",
      type: "spiked-circle",
      style: "--icon-color: var(--color-blue-primary-60);",
    },
    "exam-voucher": {
      text: "Exam Voucher",
      type: "color-stars",
    },
    "practice-exam": {
      text: "Practice Exam",
      type: "curved-rectangle",
      style: "--icon-color: #f5b0ad;",
    },
  };

  // Use content.contents if available, otherwise fallback to content.catalog_data
  // Define fixed order for icon types
  const typeOrder: Record<string, number> = {
    path: 1,
    "practice-exam": 2,
    "exam-voucher": 3,
  };

  let items = [];
  if (Array.isArray(content?.contents) && content.contents.length > 0) {
    items = content.contents;
  } else if (
    Array.isArray(content?.catalog_data) &&
    content.catalog_data.length > 0
  ) {
    items = content.catalog_data;
  }

  if (content?.type === "bundle" && items.length > 0) {
    // Filter out duplicate items by their 'type' property
    const seen = new Set<string>();
    const uniqueContents = items.filter((item: any) => {
      if (!item.type) return false;
      if (seen.has(item.type)) return false;
      seen.add(item.type);
      return true;
    });

    // Sort based on defined order
    uniqueContents.sort(
      (a: any, b: any) =>
        (typeOrder[a.type] ?? Infinity) - (typeOrder[b.type] ?? Infinity)
    );

    return uniqueContents
      .map((item: any) => {
        const type = typesMap[item.type];
        if (type) {
          return {
            type: type.type,
            text: type.text,
            style: type.style, // Optional style for custom CSS
          };
        }
        return;
      })
      .filter((icon: BuildIcon | undefined) => icon !== undefined);
  }
}

export function IconFree(
  content: any,
  isDetailPage?: boolean
): BuildIcon | undefined {
  let end: string | undefined;
  if (content?.guid) {
    const mainGuids = Statsig.getConfig<string[]>(
      "REVUP_CAMPAIGN",
      "REV_UP_CAMPAIGN_GUIDs"
    );
    const dcfnduGuids = Array.isArray(
      globalThis.env.REV_UP_CAMPAIGN_GUIDS_DCFNDU
    )
      ? globalThis.env.REV_UP_CAMPAIGN_GUIDS_DCFNDU
      : [];

    if (mainGuids.includes(content.guid)) {
      end = Statsig.getConfig<string>("REVUP_CAMPAIGN", "REV_UP_CAMPAIGN_END");
    } else if (dcfnduGuids.includes(content.guid)) {
      end = globalThis.env.REV_UP_CAMPAIGN_END_DCFNDU;
    }
  }
  if (end) {
    return {
      type: "event",
      text:
        "Free until " +
        (isDetailPage
          ? freeCampaignDatePacificTime(end)
          : freeCampaignDateShortPacificTime(end)),
    };
  }
  return undefined;
}

function IconCertificationLevel(content: any): BuildIcon | undefined {
  let certLevel;
  if (
    content.lab_type == "graded-labs" ||
    content.lab_type == "standalone-labs" ||
    content.lab_type == "ccie-practice-labs"
  ) {
    // For graded-labs, standalone-labs, and ccie-practice-labs, get level from certifications_data array
    if (
      Array.isArray(content?.certifications_data) &&
      content.certifications_data.length > 0
    ) {
      certLevel = content.certifications_data[0].certification_level;
    } else {
      certLevel = Array.isArray(content?.certification_levels)
        ? content.certification_levels[0]
        : content?.certification_levels;
    }
  } else {
    certLevel = Array.isArray(content?.certification_level)
      ? content.certification_level[0]
      : content?.certification_level;
  }

  const level = certLevel;
  if (level) {
    let iconType: string;

    switch (level) {
      case "Associate":
        iconType = "associate";
        break;
      case "Expert":
        iconType = "expert";
        break;
      case "Professional":
        iconType = "professional";
        break;
      default:
        iconType = "novice";
    }
    return {
      type: iconType,
      text: level,
    };
  }
  return undefined;
}

function IconCalendarRange(content: any): BuildIcon | undefined {
  if (!content) return undefined;
  const globalProduct = content?.global_products?.find(
    (product: any) => product?.is_single === 1
  );
  if (globalProduct?.ga_date && globalProduct?.eos_date) {
    return MergeDate(globalProduct?.ga_date, globalProduct?.eos_date);
  } else if (content?.ga_date && content?.eos_date) {
    return MergeDate(content?.ga_date, content?.eos_date);
  }
  return undefined;
}

export function IconLanguage(content: any): BuildIcon | undefined {
  const languageName = getByTag(content.language_code?.split("-")[0])?.name;
  if (languageName) {
    return {
      type: "globe",
      text: `Languages: ${languageName}`,
    };
  }
  return undefined;
}

function MergeDate(startDate: any, endDate: any): any {
  const startYear = new Date(startDate).getFullYear();
  const endYear = new Date(endDate).getFullYear();

  if (startYear === endYear) {
    return {
      type: "event",
      text:
        dateFormats.longMonthDayNoYearFmtUtc(startDate) +
        " to " +
        dateFormats.longMonthDayNoYearFmtUtc(endDate) +
        ", " +
        startYear,
    };
  } else {
    return {
      type: "event",
      text:
        dateFormats.longMonthFmtUtc(startDate) +
        " to " +
        dateFormats.longMonthFmtUtc(endDate),
    };
  }
}

export function IconRetire(content: any): BuildIcon | undefined {
  if (!content) return undefined;

  const retiring = content?.content_retired;
  if (retiring === "NOT_RETIRED") {
    return undefined;
  }
  if (retiring === "RETIRING_SOON") {
    return {
      type: "event",
      text: `Retiring ${dateFormats.longMonthFmtUtc(content?.eol_date)}`,
    };
  }
  if (retiring === "RETIRED") {
    return {
      type: "event",
      text: `Retired ${dateFormats.longMonthFmtUtc(content?.eol_date)}`,
    };
  }
  return undefined;
}

function returnType(cType: any) {
  let rtn = "";
  const path = "learning-path";
  const videoSeries = "video-series";
  const ilt = "instructor-led";
  const practiceExam = "practice-exam";
  switch (cType) {
    case "path":
      rtn = path;
      break;
    case "webinar":
      rtn = "virtual-event";
      break;
    case "videoSeries":
    case "track":
    case videoSeries:
    case "video-track":
      rtn = videoSeries;
      break;
    case "video":
      rtn = "video";
      break;
    case "podcast":
      rtn = "podcast";
      break;
    case "course":
      rtn = "course";
      break;
    case "challenge":
      rtn = "challenge";
      break;
    case "tutorial":
      rtn = "tutorial";
      break;
    case ilt:
      rtn = ilt;
      break;
    case practiceExam:
      rtn = practiceExam;
      break;
    case "exam-voucher":
      rtn = "exam-voucher";
      break;
    case "bundle":
      rtn = "bundle";
      break;
    case "lab":
      rtn = "lab";
      break;
    default:
      rtn = path;
  }
  return rtn;
}

function retiringContent(content: any): boolean {
  if (!content) return false;
  const globalProduct = content?.global_products?.find(
    (product: any) => product?.is_single === 1
  );
  if (!globalProduct?.eos_date && !content?.eos_date) return false;
  const expiration_date = new Date(
    globalProduct?.eos_date ? globalProduct?.eos_date : content?.eos_date
  );
  expiration_date.setDate(expiration_date.getDate() - 30);
  return new Date() >= expiration_date;
}

function prioritizeDate(content: any): BuildIcon | undefined {
  const retireIcon = IconRetire(content);
  if (retireIcon) {
    return retireIcon;
  }

  const updateIcon = IconUpdate(content);
  if (updateIcon) {
    return updateIcon;
  }

  return IconCreate(content);
}

function showRevUpCampaign(content: any) {
  const campaignGuids = Statsig.getConfig<string[]>(
    "REVUP_CAMPAIGN",
    "REV_UP_CAMPAIGN_GUIDs"
  );

  const today = new Date().toLocaleString("en-US", {
    timeZone: "America/Los_Angeles",
  });
  if (campaignGuids.includes(content?.guid)) {
    const from = new Date(
      Statsig.getConfig<string>("REVUP_CAMPAIGN", "REV_UP_CAMPAIGN_START")
    );
    const to = new Date(
      Statsig.getConfig<string>("REVUP_CAMPAIGN", "REV_UP_CAMPAIGN_END")
    );
    return new Date(today) >= from && new Date(today) <= to;
  }
  const dcfnduGuids = Array.isArray(globalThis.env.REV_UP_CAMPAIGN_GUIDS_DCFNDU)
    ? globalThis.env.REV_UP_CAMPAIGN_GUIDS_DCFNDU
    : [];
  if (
    content?.guid &&
    dcfnduGuids?.length &&
    dcfnduGuids.includes(content.guid)
  ) {
    const fromDCFNDU = new Date(globalThis.env.REV_UP_CAMPAIGN_START_DCFNDU);
    const toDCFNDU = new Date(globalThis.env.REV_UP_CAMPAIGN_END_DCFNDU);
    return new Date(today) >= fromDCFNDU && new Date(today) <= toDCFNDU;
  }

  return false;
}

export function prioritizeStatusIcon(
  content: any,
  isDetailPage?: boolean
): BuildIcon | undefined {
  const freeIcon = IconFree(content, isDetailPage);
  if (freeIcon && showRevUpCampaign(content)) {
    return freeIcon;
  }

  const retireIcon = IconRetire(content);
  const updateIcon = IconUpdate(content);
  if (retireIcon) {
    return retireIcon;
  } else {
    if (updateIcon) {
      return updateIcon;
    }
    return IconCreate(content);
  }
}

export function buildGarden(
  content: any,
  iconLimit: number,
  isStoreCard?: boolean,
  isDetailPage?: boolean
): Array<any> {
  const icons = collectIcons(content, isDetailPage);
  const contentType = returnType(content?.type);

  let rtn: Array<any> = [];

  // Handle each content type with separate functions
  switch (contentType) {
    case "podcast":
      rtn = handlePodcast(icons);
      break;
    case "virtual-event":
      rtn = handleVirtualEvent(icons);
      break;
    case "learning-path":
      rtn = handleLearningPath(icons, content, isStoreCard);
      break;
    case "instructor-led":
      rtn = handleInstructorLed(icons, iconLimit);
      break;
    case "course":
      rtn = handleCourse(icons, isStoreCard);
      break;
    case "challenge":
      rtn = handleChallenge(icons, content);
      break;
    case "practice-exam":
      rtn = handlePracticeExam(icons, isStoreCard);
      break;
    case "tutorial":
      rtn = handleTutorial(icons, isStoreCard);
      break;
    case "video-track":
    case "video-series":
    case "video":
      rtn = handleVideo(icons, isStoreCard);
      break;
    case "exam-voucher":
      rtn = handleExamVoucher(icons);
      break;
    case "bundle":
      rtn = handleBundle(icons, iconLimit);
      break;
    case "lab":
      rtn = handleLab(icons, content, isDetailPage);
      break;
  }

  return rtn.filter(Boolean); // remove undefined values
}

function collectIcons(content: any, isDetailPage?: boolean): any {
  return {
    calendar: IconCalendar(content),
    difficulty: IconDifficultyType(content),
    time: IconDuration(content),
    courses: IconCourses(content),
    labs: IconLabs(content),
    nodes: IconNodes(content),
    credits: IconCreds(content),
    assessments: IconAssessments(content),
    display_version: IconVersion(content),
    certification: IconCertification(content),
    create: IconCreate(content),
    certificationLevel: IconCertificationLevel(content),
    videos: IconVideos(content),
    dateRange: IconCalendarRange(content),
    retireIcon: IconRetire(content),
    dateIcon: prioritizeDate(content),
    statusIcon: prioritizeStatusIcon(content, isDetailPage),
    freeIcon: IconFree(content, isDetailPage),
    language: IconLanguage(content),
    examFormat: IconExamFormat(content),
    bundleContentsIcons: IconBundleContents(content),
  };
}

function handlePodcast(icons: any): Array<any> {
  return [icons.time, icons.calendar];
}

function handleVirtualEvent(icons: any): Array<any> {
  return [icons.time];
}

function handleLearningPath(
  icons: any,
  content: any,
  isStoreCard?: boolean
): Array<any> {
  const rtn = [];
  if (!isStoreCard) {
    rtn.push(
      icons.assessments,
      icons.courses,
      icons.display_version,
      icons.statusIcon
    );
  }
  if (isStoreCard && icons.freeIcon && showRevUpCampaign(content)) {
    rtn.push(icons.freeIcon);
  }
  rtn.push(
    icons.difficulty,
    icons.time,
    icons.labs,
    icons.credits,
    icons.language
  );
  return rtn;
}

function handleInstructorLed(icons: any, iconLimit: number): Array<any> {
  let rtn = [icons.difficulty, icons.time].filter(Boolean);

  if (rtn.length < iconLimit) rtn.push(icons.courses);
  if (rtn.length < iconLimit) rtn.push(icons.labs);
  if (rtn.length < iconLimit) rtn.push(icons.assessments);

  rtn.push(icons.display_version, icons.create, icons.credits, icons.language);
  return rtn;
}

function handleCourse(icons: any, isStoreCard?: boolean): Array<any> {
  const rtn = [];
  if (!isStoreCard) {
    rtn.push(icons.dateIcon);
  }
  rtn.push(icons.difficulty, icons.time, icons.labs, icons.language);
  return rtn;
}

function handleChallenge(icons: any, content: any): Array<any> {
  const rtn = [icons.difficulty, icons.time, icons.language];

  if (retiringContent(content)) {
    rtn.push(icons.retireIcon);
  } else {
    rtn.push(icons.dateRange);
  }
  return rtn;
}

function handlePracticeExam(icons: any, isStoreCard?: boolean): Array<any> {
  const rtn = [];
  if (!isStoreCard) {
    rtn.push(icons.dateIcon);
  }
  rtn.push(icons.certificationLevel, icons.language);
  return rtn;
}

function handleTutorial(icons: any, isStoreCard?: boolean): Array<any> {
  const rtn = [];
  if (!isStoreCard) {
    rtn.push(icons.dateIcon);
  }
  rtn.push(icons.difficulty, icons.time, icons.language);
  return rtn;
}

function handleVideo(icons: any, isStoreCard?: boolean): Array<any> {
  const rtn = [];
  if (!isStoreCard) {
    rtn.push(icons.dateIcon);
  }
  rtn.push(icons.difficulty, icons.time, icons.videos, icons.language);
  return rtn;
}

function handleExamVoucher(icons: any): Array<any> {
  return [icons.certificationLevel, icons.examFormat];
}

function handleLab(
  icons: any,
  content: any,
  isDetailPage?: boolean
): Array<any> {
  if (
    content.lab_type === "graded-labs" ||
    content.lab_type === "ccie-practice-labs"
  ) {
    // graded-labs: duration, level (certification name shown in related-certs section)
    if (isDetailPage) {
      return [
        icons.nodes,
        icons.time,
        icons.certificationLevel,
        icons.certification,
      ];
    }
    return [icons.time, icons.certificationLevel];
  }
  if (content.lab_type === "standalone-labs") {
    // standalone-labs: duration, level, version, created (no certification name)
    if (isDetailPage) {
      return [
        icons.nodes,
        icons.display_version,
        icons.time,
        icons.certificationLevel,
        icons.create,
      ];
    }
    return [
      icons.time,
      icons.certificationLevel,
      icons.display_version,
      icons.create,
    ];
  }
  return [icons.nodes, icons.display_version];
}

function handleBundle(icons: any, iconLimit: number): Array<any> {
  const rtn = [];
  if (icons.bundleContentsIcons?.length > 0) {
    for (const icon of icons.bundleContentsIcons) {
      if (icon && rtn.length < iconLimit) {
        rtn.push(icon);
      }
    }
  }
  return rtn;
}

export default buildGarden;
